import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

function apiErr(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) return String((e as { message: string }).message);
  return 'Something went wrong. Please try again.';
}

export function ServiceArea() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [zipInput, setZipInput] = useState('');
  const [zipSet, setZipSet] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['service-area'],
    queryFn: () => api.get<{ data: { zip_codes: string[] } }>('/contractor/service-area'),
  });

  useEffect(() => {
    if (data?.data?.zip_codes) {
      setZipSet(new Set(data.data.zip_codes));
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (zipCodes: string[]) =>
      api.post<{ data: { count: number } }>('/contractor/service-area', { zip_codes: zipCodes }),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      queryClient.invalidateQueries({ queryKey: ['service-area'] });
    },
    onError: (e) => setError(apiErr(e)),
  });

  function addZips() {
    setError('');
    const incoming = zipInput
      .split(/[\s,\n]+/)
      .map(z => z.trim())
      .filter(z => /^\d{5}$/.test(z));

    if (incoming.length === 0) {
      setError('Enter valid 5-digit ZIP codes separated by commas or spaces.');
      return;
    }

    setZipSet(prev => {
      const next = new Set(prev);
      incoming.forEach(z => next.add(z));
      return next;
    });
    setZipInput('');
  }

  function removeZip(zip: string) {
    setZipSet(prev => {
      const next = new Set(prev);
      next.delete(zip);
      return next;
    });
  }

  function save() {
    setError('');
    const zips = Array.from(zipSet).sort();
    if (zips.length === 0) {
      setError('Add at least one ZIP code before saving.');
      return;
    }
    saveMutation.mutate(zips);
  }

  const sortedZips = Array.from(zipSet).sort();

  return (
    <div className="min-h-dvh bg-gray-50 pb-10">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-700 to-blue-900 pt-12 pb-6 px-4">
        <button
          onClick={() => navigate('/app/contractor/dashboard')}
          className="flex items-center gap-1 text-white/80 text-sm font-medium mb-4"
          style={{ minHeight: 44 }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Dashboard
        </button>
        <h1 className="text-white text-2xl font-bold">Service Area</h1>
        <p className="text-white/70 text-sm mt-1">
          {sortedZips.length} ZIP code{sortedZips.length !== 1 ? 's' : ''} covered
        </p>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Add ZIPs */}
        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Add ZIP Codes</p>
          <textarea
            value={zipInput}
            onChange={e => setZipInput(e.target.value)}
            placeholder="31553, 31554, 31557&#10;Or paste a list — one per line or comma separated"
            rows={4}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <button
            onClick={addZips}
            className="w-full py-2.5 bg-blue-700 text-white rounded-xl text-sm font-bold"
          >
            Add ZIP Codes
          </button>
        </div>

        {/* ZIP list */}
        {isLoading && (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {sortedZips.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                {sortedZips.length} ZIP Code{sortedZips.length !== 1 ? 's' : ''}
              </p>
              <button
                onClick={() => setZipSet(new Set())}
                className="text-xs text-red-600 font-semibold"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {sortedZips.map(zip => (
                <div
                  key={zip}
                  className="flex items-center gap-1.5 bg-blue-50 text-blue-800 text-sm font-semibold px-3 py-1.5 rounded-full"
                >
                  {zip}
                  <button
                    onClick={() => removeZip(zip)}
                    className="text-blue-400 hover:text-blue-700 leading-none"
                    aria-label={`Remove ${zip}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {sortedZips.length === 0 && !isLoading && (
          <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
            <p className="text-gray-400 text-sm">No ZIP codes added yet.</p>
            <p className="text-gray-400 text-xs mt-1">Add the ZIP codes where you take jobs above.</p>
          </div>
        )}

        {/* Save */}
        <button
          onClick={save}
          disabled={saveMutation.isPending || sortedZips.length === 0}
          className={`w-full py-4 rounded-2xl text-white font-bold text-base transition-colors ${
            saved ? 'bg-green-600' : 'bg-blue-700 disabled:bg-blue-300'
          }`}
        >
          {saveMutation.isPending ? 'Saving…' : saved ? '✓ Saved!' : `Save ${sortedZips.length} ZIP Code${sortedZips.length !== 1 ? 's' : ''}`}
        </button>

        <p className="text-center text-xs text-gray-400 px-4">
          Homeowners in these ZIP codes will see you when they request a contractor for tasks matching your trade.
        </p>
      </div>
    </div>
  );
}
